import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { miljo } from '../felles/miljo.js';

let manager: UserManager | null = null;

/**
 * Stien appen kjører under: tom for Enklere styring, `/vedlikehold` for
 * vedlikeholdsappen. De to deler innlogging og opphav, men innloggingen må
 * sende brukeren tilbake til appen hen kom fra.
 */
export function grunnsti(sti = window.location.pathname): string {
  return sti === '/vedlikehold' || sti.startsWith('/vedlikehold/') ? '/vedlikehold' : '';
}

/**
 * OIDC login against our own identity broker.
 *
 * Authorization Code Flow with PKCE, no client secret. The app talks to the
 * broker's discovery document only — it has no knowledge of Keycloak
 * specifically, so pointing it at a different broker, or federating a kommune to
 * Entra ID or Feide, is a configuration change here and in Keycloak, never a
 * code change (§3.1).
 *
 * On token storage: tokens live in sessionStorage, so they are scoped to the tab
 * and cleared when it closes. This is the standard trade-off for a public SPA
 * client — it is exposed to XSS in a way an httpOnly cookie would not be, which
 * is why the app ships with a strict CSP and why access tokens live only five
 * minutes with refresh rotation. If a future risk assessment for a kommune finds
 * this insufficient, the hardening step is a backend-for-frontend holding the
 * tokens server-side; that changes this file and nothing else, because the rest
 * of the app only ever asks `hentToken()` for a string.
 */
/**
 * Created lazily, on first use, because the configuration it needs is fetched at
 * runtime — a module-level instance would be constructed at import time, before
 * `lastMiljo()` has resolved.
 */
export function brukerManager(): UserManager {
  if (manager) return manager;
  const konfig = miljo();
  manager = new UserManager({
    authority: konfig.oidcIssuerUrl,
    client_id: konfig.oidcClientId,
    redirect_uri: `${window.location.origin}${grunnsti()}/auth/tilbake`,
    post_logout_redirect_uri: `${window.location.origin}${grunnsti()}`,
    response_type: 'code',
    scope: 'openid profile email',
    // Innloggingssiden på norsk, uansett nettleser. Realmen har norsk som
    // standard, men Keycloak går etter språket nettleseren ber om, og en
    // nettleser på engelsk ga en engelsk innloggingsside i en norsk løsning.
    // «no», ikke «nb»: Keycloak har oversettelsene sine under «no», og en kode
    // den ikke kjenner, gir engelsk.
    ui_locales: 'no',
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    // Renew before the five-minute access token expires, so a user working through
    // a long forecast edit is never bounced to the login screen mid-task.
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 60,
    monitorSession: false,
  });
  return manager;
}

export async function hentBruker(): Promise<User | null> {
  const bruker = await brukerManager().getUser();
  if (!bruker || bruker.expired) return null;
  return bruker;
}

export async function loggInn(idpHint?: string): Promise<void> {
  await brukerManager().signinRedirect({
    // `kc_idp_hint` sends the user straight to their kommune's own identity
    // provider instead of the broker's chooser screen. Which hint to send comes
    // from the kommune's configuration, not from anything hardcoded here.
    ...(idpHint ? { extraQueryParams: { kc_idp_hint: idpHint } } : {}),
  });
}

export async function loggUt(): Promise<void> {
  await brukerManager().signoutRedirect();
}

let innloggingUnderveis: Promise<User> | null = null;

/**
 * Completes the OIDC redirect, exactly once per page load.
 *
 * The guard is essential, not defensive. An OAuth authorization code is
 * SINGLE-USE: the first exchange succeeds and the broker invalidates the code, so
 * any second attempt gets a 400. Without this, the callback route reports a
 * failure for a login that actually worked — the session is in storage, and the
 * user is looking at an error.
 *
 * It fires more than once for entirely ordinary reasons: React StrictMode invokes
 * effects twice in development, and Fast Refresh can remount the route. Caching
 * the promise makes every caller share the one exchange.
 *
 * A rejection is cached too, and deliberately: a failed exchange cannot be
 * retried with the same code, so the only real recovery is to start the login
 * over — which is what the screen offers.
 */
export function fullforInnlogging(): Promise<User> {
  innloggingUnderveis ??= brukerManager().signinRedirectCallback();
  return innloggingUnderveis;
}

/** Test seam: clears the cached exchange between cases. */
export function nullstillInnlogging(): void {
  innloggingUnderveis = null;
}

export async function hentToken(): Promise<string | null> {
  const bruker = await hentBruker();
  return bruker?.access_token ?? null;
}
